import axios from 'axios';
import { getToken, removeToken } from '../utils/authStorage';

// Dynamically resolve API URL so it works on any device on the same WiFi.
// If REACT_APP_API_URL is set (production), use that.
// Otherwise use the same hostname the browser is accessing — works for
// localhost AND for phone/tablet on same WiFi (e.g. 192.168.1.x:5000).
function getBaseURL() {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  // Dev-only fallback: only derive :5000 on localhost/LAN
  // On production hosts (Vercel etc.) without env var, use same-origin /api
  // to avoid silently hitting wrong backend
  const { protocol, hostname } = window.location;
  const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1' ||
    /^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[01])\./.test(hostname);
  if (isLocalDev) return `${protocol}//${hostname}:5000/api`;
  // Production without env var: warn and use same-origin /api (requires reverse proxy)
  console.warn('[API] REACT_APP_API_URL not set in production. Falling back to same-origin /api — ensure reverse proxy is configured.');
  return '/api';
}

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 45000,  // 45s — accounts for Render cold start
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    
    // Device fingerprint
    const fingerprint = localStorage.getItem('device_fingerprint');
    if (fingerprint) config.headers['X-Device-Fingerprint'] = fingerprint;
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // If user just logged in (within 3s), don't auto-logout — avoids race condition
      if (window.__justLoggedIn) return Promise.reject(error);

      // Only redirect to login if user is already logged in (has token)
      // If on login page, 401 = wrong password — DO NOT redirect, let the page handle it
      const onAuthPage = window.location.pathname === '/login' || 
                         window.location.pathname === '/signup' ||
                         window.location.pathname.startsWith('/forgot') ||
                         window.location.pathname.startsWith('/reset');
      if (!onAuthPage) {
        // Check if we're already logging out (token already removed)
        const stillHasToken = getToken();
        if (stillHasToken) {
          removeToken();
          sessionStorage.clear();
          // Fire event — AuthContext listens and handles navigate without hard reload
          window.dispatchEvent(new CustomEvent('auth:logout-required'));
        }
      }
    } else if (error.response?.status === 503) {
      // Fire a custom event — each page handles it with a friendly popup, not a redirect
      const errData = error.response?.data || {};
      window.dispatchEvent(new CustomEvent('service-unavailable', {
        detail: {
          message: errData.message || errData.error || 'Service temporarily unavailable',
          service: errData.service || 'service',
          open_time: errData.open_time || null,
          close_time: errData.close_time || null,
        }
      }));
    }
    return Promise.reject(error);
  }
);

export default api;

// Generate device fingerprint
export async function getDeviceFingerprint() {
  let fingerprint = localStorage.getItem('device_fingerprint');
  if (fingerprint) return fingerprint;

  // Simple fingerprint based on browser characteristics
  const data = [
    navigator.userAgent,
    navigator.language,
    window.screen.width,
    window.screen.height,
    window.screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    navigator.platform
  ].join('|');

  // Create a simple hash
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  // NOTE: Do not include Date.now() - fingerprint must be stable for same device
  fingerprint = `fp_${Math.abs(hash).toString(36)}`;
  localStorage.setItem('device_fingerprint', fingerprint);
  return fingerprint;
}
